/**
 * ImportHistoryPage - Liste des imports passés avec filtres et actions
 * Design V2: Glassmorphism + Teal Color Scheme (Dashboard context)
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  History,
  Upload,
  Filter,
  RefreshCw,
  Download,
  RotateCcw,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  AlertCircle,
  FileSpreadsheet,
  ChevronLeft,
  ChevronRight,
  Database,
  Eye,
} from 'lucide-react';
import type { ImportType, ImportStatus, Import } from '../types/import.types';
import { useImportHistory, useRollbackImport } from '../hooks/useImport';
import { importService } from '../services/import.service';
import { confirm } from '@/stores/confirmStore';
import { showSuccess, showError, showWarning } from '@/lib/notifications';
import { logger } from '@/utils/logger';

const STATUS_CONFIG: Record<
  ImportStatus,
  { label: string; icon: typeof CheckCircle; color: string; bgColor: string }
> = {
  PENDING: { label: 'En attente', icon: Clock, color: 'text-gray-500', bgColor: 'bg-gray-100' },
  UPLOADING: { label: 'Chargement en cours', icon: Clock, color: 'text-blue-500', bgColor: 'bg-blue-100' },
  UPLOADED: { label: 'Fichier chargé', icon: CheckCircle, color: 'text-blue-500', bgColor: 'bg-blue-100' },
  PARSING: { label: 'Analyse en cours', icon: Clock, color: 'text-blue-500', bgColor: 'bg-blue-100' },
  PARSED: { label: 'Analysé', icon: CheckCircle, color: 'text-blue-500', bgColor: 'bg-blue-100' },
  VALIDATING: { label: 'Validation en cours', icon: Clock, color: 'text-amber-500', bgColor: 'bg-amber-100' },
  VALIDATED: { label: 'Validé', icon: CheckCircle, color: 'text-amber-500', bgColor: 'bg-amber-100' },
  AWAITING_CONFIRM: { label: 'En attente de confirmation', icon: Clock, color: 'text-amber-500', bgColor: 'bg-amber-100' },
  PROCESSING: { label: 'Traitement en cours', icon: Clock, color: 'text-teal-500', bgColor: 'bg-teal-100' },
  COMPLETED: { label: 'Terminé', icon: CheckCircle, color: 'text-green-500', bgColor: 'bg-green-100' },
  COMPLETED_PARTIAL: { label: 'Terminé partiellement', icon: AlertTriangle, color: 'text-amber-500', bgColor: 'bg-amber-100' },
  FAILED: { label: 'Échec', icon: XCircle, color: 'text-red-500', bgColor: 'bg-red-100' },
  CANCELLED: { label: 'Annulé', icon: XCircle, color: 'text-gray-500', bgColor: 'bg-gray-100' },
  ROLLED_BACK: { label: 'Annulé (restauré)', icon: RotateCcw, color: 'text-gray-500', bgColor: 'bg-gray-100' },
};

const TYPE_LABELS: Record<ImportType, string> = {
  CLIENT: 'Clients',
  PRODUCT: 'Produits',
  INVOICE: 'Factures',
  QUOTE: 'Devis',
  CREDIT_NOTE: 'Avoirs',
  EXPENSE: 'Dépenses',
  SUPPLIER_INVOICE: 'Fact. fournisseurs',
  FEC: 'FEC',
  BANK_STATEMENT: 'Relevés bancaires',
  BANK_TRANSACTION: 'Transactions',
  JOURNAL_ENTRY: 'Écritures',
  PAYMENT: 'Paiements',
};

export function ImportHistoryPage() {
  const navigate = useNavigate();

  // Filters
  const [typeFilter, setTypeFilter] = useState<ImportType | ''>('');
  const [statusFilter, setStatusFilter] = useState<ImportStatus | ''>('');
  const [page, setPage] = useState(1);
  const limit = 20;

  // Query
  const { data, isLoading, refetch } = useImportHistory({
    type: typeFilter || undefined,
    status: statusFilter || undefined,
    page,
    limit,
  });

  // Mutations
  const rollbackMutation = useRollbackImport();

  // Handlers
  const handleDownload = useCallback(async (importRecord: Import) => {
    try {
      const blob = await importService.downloadFile(importRecord.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = importRecord.originalFileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      logger.error('Download failed:', error);
    }
  }, []);

  const handleRollback = useCallback(async (importRecord: Import) => {
    const confirmed = await confirm({
      title: 'Annuler cet import ?',
      message: `Vous êtes sur le point d'annuler l'import "${importRecord.originalFileName}". Cette action supprimera toutes les données importées et ne peut pas être annulée.`,
      confirmText: 'Oui, annuler l\'import',
      cancelText: 'Non, conserver',
      variant: 'danger',
    });

    if (!confirmed) return;

    try {
      await rollbackMutation.mutateAsync(importRecord.id);
      // Le rollback est asynchrone - on poll le statut pour voir le résultat
      let attempts = 0;
      const maxAttempts = 10;
      const pollInterval = setInterval(async () => {
        attempts++;
        await refetch();
        const updatedImports = data?.imports || [];
        const updatedImport = updatedImports.find((i) => i.id === importRecord.id);

        if (updatedImport?.status === 'ROLLED_BACK') {
          clearInterval(pollInterval);
          showSuccess('Import annulé avec succès. Les données importées ont été supprimées.');
        } else if (updatedImport?.status === 'FAILED' || attempts >= maxAttempts) {
          clearInterval(pollInterval);
          if (attempts >= maxAttempts) {
            showWarning('L\'annulation est en cours de traitement. Veuillez rafraîchir la page dans quelques instants.');
          } else {
            showError(`L'annulation a échoué : ${updatedImport?.errorMessage || 'Une erreur inattendue s\'est produite. Veuillez réessayer.'}`);
          }
        }
      }, 1500);
    } catch (error) {
      logger.error('Rollback failed:', error);
      showError('Une erreur est survenue lors de l\'annulation de l\'import. Veuillez réessayer.');
    }
  }, [rollbackMutation, refetch, data?.imports]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const totalPages = data ? Math.ceil(data.total / limit) : 1;

  // Calculate KPI stats
  const imports = data?.imports || [];
  const totalImports = data?.total || 0;
  const completedImports = imports.filter(i => i.status === 'COMPLETED' || i.status === 'COMPLETED_PARTIAL').length;
  const pendingImports = imports.filter(i => i.status === 'PROCESSING' || i.status === 'PENDING' || i.status === 'VALIDATING').length;
  const errorImports = imports.filter(i => i.status === 'FAILED').length;

  return (
    <div className="px-8 pt-8 pb-12 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-teal-100 rounded-2xl flex items-center justify-center">
            <History className="w-7 h-7 text-teal-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Historique des imports</h1>
            <p className="text-gray-500">
              {totalImports} import(s) au total
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate('/imports')}
          className="flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-xl font-medium hover:bg-teal-700 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
        >
          <Upload className="w-5 h-5" />
          Nouvel import
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white/20 backdrop-blur-xl rounded-3xl p-6 border border-white/30 shadow-xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
              <Database className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total imports</p>
              <p className="text-2xl font-bold text-gray-900">{totalImports}</p>
            </div>
          </div>
        </div>

        <div className="bg-white/20 backdrop-blur-xl rounded-3xl p-6 border border-white/30 shadow-xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-100 rounded-xl flex items-center justify-center">
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">En cours</p>
              <p className="text-2xl font-bold text-gray-900">{pendingImports}</p>
            </div>
          </div>
        </div>

        <div className="bg-white/20 backdrop-blur-xl rounded-3xl p-6 border border-white/30 shadow-xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Réussis</p>
              <p className="text-2xl font-bold text-green-600">{completedImports}</p>
            </div>
          </div>
        </div>

        <div className="bg-white/20 backdrop-blur-xl rounded-3xl p-6 border border-white/30 shadow-xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Échecs</p>
              <p className="text-2xl font-bold text-red-600">{errorImports}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white/20 backdrop-blur-xl rounded-3xl p-6 border border-white/30 shadow-xl">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-teal-600" />
            <span className="text-sm font-medium text-gray-700">Filtres:</span>
          </div>

          <select
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value as ImportType | '');
              setPage(1);
            }}
            className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-900 bg-white focus:ring-2 focus:ring-teal-500/50 focus:border-transparent transition-all duration-300"
          >
            <option value="">Tous les types</option>
            {Object.entries(TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as ImportStatus | '');
              setPage(1);
            }}
            className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-900 bg-white focus:ring-2 focus:ring-teal-500/50 focus:border-transparent transition-all duration-300"
          >
            <option value="">Tous les statuts</option>
            {Object.entries(STATUS_CONFIG).map(([value, config]) => (
              <option key={value} value={value}>{config.label}</option>
            ))}
          </select>

          <button
            onClick={() => refetch()}
            className="p-2.5 hover:bg-white/50 rounded-xl transition-all duration-300"
            title="Rafraîchir"
          >
            <RefreshCw className={`w-5 h-5 text-teal-600 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white/20 backdrop-blur-xl rounded-3xl p-6 md:p-8 border border-white/30 shadow-xl">
        <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
          <FileSpreadsheet className="w-5 h-5 text-teal-600" />
          Liste des imports
        </h3>

        {isLoading ? (
          <div className="flex items-center justify-center p-12">
            <div className="w-8 h-8 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : imports.length === 0 ? (
          <div className="text-center py-12 space-y-4">
            <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto">
              <FileSpreadsheet className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-gray-500">Aucun import trouvé</p>
            <button
              onClick={() => navigate('/imports')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-xl font-medium hover:bg-teal-700 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
            >
              <Upload className="w-5 h-5" />
              Créer votre premier import
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Fichier
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Résultat
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {imports.map((importRecord) => {
                    const statusConfig = STATUS_CONFIG[importRecord.status] || STATUS_CONFIG.PENDING;
                    const StatusIcon = statusConfig.icon;

                    return (
                      <tr key={importRecord.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-teal-50 rounded-xl flex items-center justify-center">
                              <FileSpreadsheet className="w-5 h-5 text-teal-600" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900 truncate max-w-[200px]" title={importRecord.originalFileName}>
                                {importRecord.originalFileName}
                              </p>
                              <p className="text-xs text-gray-500">
                                {formatFileSize(importRecord.originalFileSize)}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span className="px-2 py-1 bg-teal-100 text-teal-700 rounded-lg text-xs font-medium">
                            {TYPE_LABELS[importRecord.type] || importRecord.type}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-lg ${statusConfig.bgColor} ${statusConfig.color}`}>
                            <StatusIcon className="w-3 h-3" />
                            {statusConfig.label}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm">
                            <span className="text-green-600 font-medium">{importRecord.successCount}</span>
                            <span className="text-gray-400"> / </span>
                            <span className="text-gray-900">{importRecord.totalRows}</span>
                            {importRecord.errorCount > 0 && (
                              <span className="text-red-500 ml-2 text-xs">
                                ({importRecord.errorCount} err.)
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <p className="text-sm text-gray-900">
                            {formatDate(importRecord.createdAt)}
                          </p>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleDownload(importRecord)}
                              className="flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-200 transition-colors"
                              title="Télécharger le fichier"
                            >
                              <Download className="w-3 h-3" />
                              <span className="hidden sm:inline">Télécharger</span>
                            </button>
                            {(importRecord.status === 'UPLOADED' || importRecord.status === 'PARSED' || importRecord.status === 'VALIDATED' || importRecord.status === 'PENDING') && (
                              <button
                                onClick={() => navigate(`/imports?resumeId=${importRecord.id}`)}
                                className="flex items-center gap-1 px-2 py-1 bg-teal-100 text-teal-700 rounded-lg text-xs font-medium hover:bg-teal-200 transition-colors"
                              >
                                <Eye className="w-3 h-3" />
                                Reprendre
                              </button>
                            )}
                            {importRecord.canRollback && (
                              <button
                                onClick={() => handleRollback(importRecord)}
                                disabled={rollbackMutation.isPending}
                                className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-lg text-xs font-medium hover:bg-red-200 transition-colors disabled:opacity-50"
                                title="Annuler l'import (rollback)"
                              >
                                <RotateCcw className="w-3 h-3" />
                                <span className="hidden sm:inline">Rollback</span>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-4 border-t border-gray-200/50 mt-4">
                <p className="text-sm text-gray-500">
                  Page {page} sur {totalPages} ({totalImports} résultats)
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="flex items-center gap-1 px-3 py-2 bg-white/50 hover:bg-white/70 rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4 text-gray-600" />
                    <span className="text-sm text-gray-600">Précédent</span>
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="flex items-center gap-1 px-3 py-2 bg-white/50 hover:bg-white/70 rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="text-sm text-gray-600">Suivant</span>
                    <ChevronRight className="w-4 h-4 text-gray-600" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
